import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { 
  ArrowLeft, Edit2, Save, X, Trash2, 
  Package, DollarSign, Clock, MapPin, Shield, FileText 
} from 'lucide-react';
import AdjustMedicationModal from '@/components/AdjustMedicationModal';

interface Medication {
  medication_id: string;
  org_id: string;
  drug_name: string;
  generic_name: string;
  brand_name: string | null;
  ndc_code: string;
  strength: string;
  dosage_form: string;
  dea_schedule: string;
  is_controlled: boolean;
  lot_number: string | null;
  expiration_date: string | null;
  quantity_on_hand: number;
  unit_of_measure: string;
  par_level_min: number | null;
  par_level_max: number | null;
  unit_cost: number;
  storage_location: string;
  storage_requirements: string | null;
  notes: string | null;
  days_until_expiry: number | null;
  stock_status: string;
  needs_reorder: boolean;
  active_alerts_count: number;
  total_value: number;
  created_at: string;
  updated_at: string;
}

interface Transaction {
  transaction_id: string;
  transaction_type: string;
  quantity_change: number;
  quantity_after: number;
  lot_number: string | null;
  reference_type: string | null;
  reference_id: string | null;
  notes: string | null;
  performed_by_name: string;
  witness_name: string | null;
  created_at: string;
}

export default function MedicationDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Medication>>({});

  const { data: medication, isLoading } = useQuery({
    queryKey: ['or-medication', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('or_medications')
        .select('*')
        .eq('medication_id', id)
        .single();
      if (error) throw error;
      return data as Medication;
    },
    enabled: !!id,
  });

  const { data: transactions = [] } = useQuery({
    queryKey: ['or-medication-transactions', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('or_medication_transactions')
        .select('*')
        .eq('medication_id', id)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as Transaction[];
    },
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Medication>) => {
      const { data, error } = await supabase
        .from('or_medications')
        .update(updates)
        .eq('medication_id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['or-medication', id] });
      queryClient.invalidateQueries({ queryKey: ['or-medications'] });
      setIsEditing(false);
      setEditForm({});
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('or_medications')
        .update({ quantity_on_hand: 0 })
        .eq('medication_id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      navigate('/medications');
    },
  });

  const handleEdit = () => {
    setEditForm(medication || {});
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!editForm) return;
    updateMutation.mutate(editForm);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this medication? This will set quantity to 0.')) {
      deleteMutation.mutate();
    }
  };

  const getScheduleBadgeColor = (schedule: string) => {
    switch (schedule) {
      case 'Schedule II': return 'bg-pt-red/20 text-pt-red border-pt-red/30';
      case 'Schedule III': return 'bg-pt-orange/20 text-pt-orange border-pt-orange/30';
      case 'Schedule IV': return 'bg-pt-amber/20 text-pt-amber border-pt-amber/30';
      case 'Schedule V': return 'bg-pt-cyan/20 text-pt-cyan border-pt-cyan/30';
      default: return 'bg-pt-text-dim/20 text-pt-text border-pt-input-border';
    }
  };

  if (isLoading || !medication) {
    return (
      <div className="min-h-full bg-pt-darker flex items-center justify-center">
        <div className="text-pt-cyan text-lg">Loading medication...</div>
      </div>
    );
  }

  const parLevelPercent = medication.par_level_max 
    ? (medication.quantity_on_hand / medication.par_level_max) * 100 
    : 0;

  return (
    <div className="min-h-full bg-pt-darker text-pt-text p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/medications')}
            className="p-2 hover:bg-pt-panel/50 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-pt-text mb-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              {medication.drug_name}
            </h1>
            <p className="text-pt-text-muted text-sm">
              {medication.generic_name}
              {medication.brand_name && ` • ${medication.brand_name}`}
            </p>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getScheduleBadgeColor(medication.dea_schedule)}`}>
            {medication.is_controlled ? medication.dea_schedule : 'Non-Controlled'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-pt-panel hover:bg-pt-panel text-pt-text rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                <Edit2 size={16} />
                Edit
              </button>
              <button
                onClick={() => setShowAdjustModal(true)}
                className="px-4 py-2 bg-pt-gold hover:bg-pt-gold/80 text-pt-darker rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                <Package size={16} />
                Adjust Inventory
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-pt-red hover:bg-pt-red text-pt-text rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-pt-panel hover:bg-pt-panel text-pt-text rounded-lg font-semibold flex items-center gap-2 transition-colors"
              >
                <X size={16} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="px-4 py-2 bg-pt-green hover:bg-pt-green text-pt-text rounded-lg font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                <Save size={16} />
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {medication.is_controlled && (
        <div className="bg-pt-red/10 border border-pt-red/30 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Shield className="text-pt-red mt-0.5" size={20} />
          <div>
            <div className="text-pt-red font-semibold mb-1">DEA Controlled Substance</div>
            <div className="text-pt-red text-sm">
              {medication.dea_schedule} — Witness required for all transactions. Maintain strict accountability and documentation.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
            <h2 className="text-lg font-semibold text-pt-text mb-4 flex items-center gap-2">
              <FileText size={18} className="text-pt-cyan" />
              Medication Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">Drug Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.drug_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, drug_name: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text"
                  />
                ) : (
                  <div className="mt-1 text-pt-text">{medication.drug_name}</div>
                )}
              </div>
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">Generic Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.generic_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, generic_name: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text"
                  />
                ) : (
                  <div className="mt-1 text-pt-text">{medication.generic_name}</div>
                )}
              </div>
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">Brand Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.brand_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, brand_name: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text"
                  />
                ) : (
                  <div className="mt-1 text-pt-text">{medication.brand_name || '—'}</div>
                )}
              </div>
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">NDC Code</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.ndc_code || ''}
                    onChange={(e) => setEditForm({ ...editForm, ndc_code: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text font-mono"
                  />
                ) : (
                  <div className="mt-1 text-pt-text font-mono">{medication.ndc_code}</div>
                )}
              </div>
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">Strength</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.strength || ''}
                    onChange={(e) => setEditForm({ ...editForm, strength: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text"
                  />
                ) : (
                  <div className="mt-1 text-pt-text">{medication.strength}</div>
                )}
              </div>
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">Dosage Form</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.dosage_form || ''}
                    onChange={(e) => setEditForm({ ...editForm, dosage_form: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text"
                  />
                ) : (
                  <div className="mt-1 text-pt-text">{medication.dosage_form}</div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
            <h2 className="text-lg font-semibold text-pt-text mb-4 flex items-center gap-2">
              <Clock size={18} className="text-pt-cyan" />
              Lot & Expiration Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">Lot Number</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.lot_number || ''}
                    onChange={(e) => setEditForm({ ...editForm, lot_number: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text font-mono"
                  />
                ) : (
                  <div className="mt-1 text-pt-text font-mono">{medication.lot_number || '—'}</div>
                )}
              </div>
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">Expiration Date</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={editForm.expiration_date || ''}
                    onChange={(e) => setEditForm({ ...editForm, expiration_date: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-pt-darker/50 border-pt-border rounded-lg text-pt-text"
                  />
                ) : (
                  <div className="mt-1">
                    {medication.expiration_date ? (
                      <>
                        <div className="text-pt-text">
                          {new Date(medication.expiration_date).toLocaleDateString()}
                        </div>
                        {medication.days_until_expiry !== null && (
                          <div className={`text-sm mt-1 ${
                            medication.days_until_expiry < 0 ? 'text-pt-red' :
                            medication.days_until_expiry < 30 ? 'text-pt-amber' :
                            'text-pt-text-muted'
                          }`}>
                            {medication.days_until_expiry < 0 
                              ? `Expired ${Math.abs(medication.days_until_expiry)} days ago`
                              : `Expires in ${medication.days_until_expiry} days`
                            }
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-pt-text0">—</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
            <h2 className="text-lg font-semibold text-pt-text mb-4 flex items-center gap-2">
              <MapPin size={18} className="text-pt-cyan" />
              Storage Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">Storage Location</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.storage_location || ''}
                    onChange={(e) => setEditForm({ ...editForm, storage_location: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text"
                  />
                ) : (
                  <div className="mt-1 text-pt-text">{medication.storage_location}</div>
                )}
              </div>
              <div>
                <label className="text-xs text-pt-text-muted uppercase tracking-wider">Storage Requirements</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.storage_requirements || ''}
                    onChange={(e) => setEditForm({ ...editForm, storage_requirements: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text"
                  />
                ) : (
                  <div className="mt-1 text-pt-text">{medication.storage_requirements || '—'}</div>
                )}
              </div>
            </div>
          </div>

          {(medication.notes || isEditing) && (
            <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-pt-text mb-4">Notes</h2>
              {isEditing ? (
                <textarea
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-pt-darker/50 border border-pt-border rounded-lg text-pt-text"
                />
              ) : (
                <div className="text-pt-text">{medication.notes}</div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
            <h2 className="text-lg font-semibold text-pt-text mb-4 flex items-center gap-2">
              <Package size={18} className="text-pt-cyan" />
              Inventory Status
            </h2>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-pt-text-muted">Quantity on Hand</span>
                  <span className="text-2xl font-bold text-pt-text">{medication.quantity_on_hand}</span>
                </div>
                <div className="text-xs text-pt-text-muted">{medication.unit_of_measure}</div>
              </div>
              {medication.par_level_max && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-pt-text-muted">Par Level</span>
                    <span className="text-sm text-pt-text">
                      {medication.par_level_min} - {medication.par_level_max}
                    </span>
                  </div>
                  <div className="w-full bg-pt-panel/50 rounded-full h-2">
                    <div
                      className={`h-2 rounded transition-all ${parLevelPercent < (medication.par_level_min || 0) / (medication.par_level_max || 1) * 100 ? 'bg-pt-red' : parLevelPercent < 70 ? 'bg-pt-amber' : 'bg-pt-green'}`}
                      style={{ width: `${Math.min(parLevelPercent, 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-pt-text-muted mt-1">{parLevelPercent.toFixed(0)}% of max</div>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-pt-text-muted">Stock Status</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    medication.stock_status === 'Out of Stock' ? 'bg-pt-red/20 text-pt-red' :
                    medication.stock_status === 'Low Stock' ? 'bg-pt-amber/20 text-pt-amber' :
                    medication.stock_status === 'Overstocked' ? 'bg-pt-cyan/20 text-pt-cyan' :
                    'bg-pt-green/20 text-pt-green'
                  }`}>
                    {medication.stock_status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
            <h2 className="text-lg font-semibold text-pt-text mb-4 flex items-center gap-2">
              <DollarSign size={18} className="text-pt-cyan" />
              Costing
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-pt-text-muted">Unit Cost</span>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.unit_cost || ''}
                    onChange={(e) => setEditForm({ ...editForm, unit_cost: parseFloat(e.target.value) })}
                    className="w-24 px-2 py-1 bg-pt-darker/50 border border-pt-border rounded text-pt-text text-right"
                  />
                ) : (
                  <span className="text-pt-text font-medium">${medication.unit_cost.toFixed(2)}</span>
                )}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-pt-border">
                <span className="text-sm text-pt-text-muted">Total Value</span>
                <span className="text-lg font-bold text-pt-gold">${medication.total_value.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-pt-dark/30 border border-pt-border rounded-lg p-6">
        <h2 className="text-lg font-semibold text-pt-text mb-4">Transaction History</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-pt-darker/50 border-b border-pt-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase">After</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase">Performed By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase">Witness</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-pt-text-muted uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-pt-border">
              {transactions.map((tx) => (
                <tr key={tx.transaction_id}>
                  <td className="px-4 py-3 text-sm text-pt-text">
                    {new Date(tx.created_at).toLocaleDateString()} {new Date(tx.created_at).toLocaleTimeString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                      tx.transaction_type === 'Received' ? 'bg-pt-green/20 text-pt-green' :
                      tx.transaction_type === 'Used' ? 'bg-pt-cyan/20 text-pt-cyan' :
                      tx.transaction_type === 'Expired' ? 'bg-pt-red/20 text-pt-red' :
                      tx.transaction_type === 'Returned' ? 'bg-pt-cyan/20 text-pt-cyan' :
                      'bg-pt-text-dim/20 text-pt-text'
                    }`}>
                      {tx.transaction_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-medium ${tx.quantity_change > 0 ? 'text-pt-green' : 'text-pt-red'}`}>
                      {tx.quantity_change > 0 ? '+' : ''}{tx.quantity_change}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-pt-text font-medium">{tx.quantity_after}</td>
                  <td className="px-4 py-3 text-sm text-pt-text">{tx.performed_by_name}</td>
                  <td className="px-4 py-3 text-sm text-pt-text">
                    {tx.witness_name ? (
                      <span className="flex items-center gap-1">
                        <Shield size={12} className="text-pt-cyan" />
                        {tx.witness_name}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-pt-text-muted">{tx.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdjustModal && (
        <AdjustMedicationModal
          medication={medication}
          onClose={() => setShowAdjustModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['or-medication', id] });
            queryClient.invalidateQueries({ queryKey: ['or-medication-transactions', id] });
            queryClient.invalidateQueries({ queryKey: ['or-medications'] });
            setShowAdjustModal(false);
          }}
        />
      )}
    </div>
  );
}
